import type { AccessoryProduct, CartItem } from "@constants/formDefaults";
import { BUNDLE_PROMO_CODES, PRODUCTS, SHIPPING_PROMO_SKUS } from "@constants/prices";
import { getDiscountAmount, getPrice, type SupportedCurrency } from "./currencyUtils";

export interface CouponData {
  id?: string;
  amountOff?: number | null;
  percentOff?: number | null;
  appliesTo?: string[];
  disqualifyingSkus?: string[];
  name?: string;
  duration?: string;
  currency?: string | null;
  metadata?: Record<string, any>;
  valid?: boolean;
}

export function calculateCartWeight({
  items,
  accessoryProducts,
  country,
}: {
  items: CartItem[];
  accessoryProducts?: AccessoryProduct[];
  country?: string;
}) {
  const isUS = country === "US";

  // Calculate total weight from all physical products
  const physicalProductWeight = items.reduce((totalWeight, item) => {
    const product = PRODUCTS[item.sku];
    if (product?.type === "physicalProduct" && product.weight) {
      return totalWeight + product.weight * item.quantity;
    }
    return totalWeight;
  }, 0);

  // Calculate weight from accessories
  const accessoryWeight = items.reduce((totalWeight, item) => {
    const accessory = accessoryProducts?.find((p) => p.SKU === item.sku);
    if (accessory?.Weight) {
      return totalWeight + accessory.Weight * item.quantity;
    }
    return totalWeight;
  }, 0);

  const totalWeight = physicalProductWeight + accessoryWeight;

  // For US customers with promo items: check if we should exclude promo weight
  if (isUS) {
    const hasPromoItem = items.some((item) => SHIPPING_PROMO_SKUS.includes(item.sku));

    if (hasPromoItem) {
      // Calculate weight excluding promo items
      const nonPromoPhysicalWeight = items.reduce((totalWeight, item) => {
        // Skip promo items
        if (SHIPPING_PROMO_SKUS.includes(item.sku)) {
          return totalWeight;
        }

        const product = PRODUCTS[item.sku];
        if (product?.type === "physicalProduct" && product.weight) {
          return totalWeight + product.weight * item.quantity;
        }
        return totalWeight;
      }, 0);

      const nonPromoWeight = nonPromoPhysicalWeight + accessoryWeight;

      // If there are non-promo items, use reduced weight
      // Otherwise (promo-only cart), return total weight (which will trigger free shipping logic elsewhere)
      if (nonPromoWeight > 0 && nonPromoWeight < totalWeight) {
        return Math.max(nonPromoWeight, 1); // Minimum 1 lb for shipping calculation
      }
    }
  }

  // For non-US customers or carts without promo: use full weight
  return Math.max(totalWeight, 1); // Minimum 1 lb
}

export function getBundleDiscounts(
  items: { sku: string; quantity: number }[],
  currency: SupportedCurrency = "usd",
) {
  const cartSkus = items.map((i) => i.sku);

  // Separate fixed and percentage discounts for proper stacking
  const fixedDiscounts = [];
  const percentageDiscounts = [];

  for (const promo of BUNDLE_PROMO_CODES) {
    const hasAllRequired = promo.requiredSkus?.every((reqSku) => cartSkus.includes(reqSku));

    if (hasAllRequired) {
      // Check requiredSomeSkus if specified
      const hasRequiredSome = !promo.requiredSomeSkus ||
        promo.requiredSomeSkus.some((sku) => cartSkus.includes(sku));

      if (!hasRequiredSome) {
        continue;
      }

      // Separate by discount type
      if (promo.discountPercentage && promo.applyToSku) {
        percentageDiscounts.push(promo);
      } else if (promo.discountAmount) {
        fixedDiscounts.push(promo);
      }
    }
  }

  // Process fixed discounts first
  const fixedDiscountItems = fixedDiscounts.map(promo => ({
    label: promo.couponName,
    amount: getDiscountAmount(promo.discountAmount, currency),
    key: promo.key,
    requiredSkus: promo.requiredSkus,
    applyToSku: (promo as any).applyToSku,
  }));

  // Process percentage discounts (apply after fixed discounts for stacking)
  const percentageDiscountItems = percentageDiscounts.map(promo => {
    const targetProduct = PRODUCTS[promo.applyToSku];

    if (!targetProduct) {
      // Fallback if product not found
      return {
        label: promo.couponName,
        amount: 0,
        key: promo.key,
        requiredSkus: promo.requiredSkus,
        applyToSku: promo.applyToSku,
      };
    }

    const productPrice = getPrice(targetProduct, "immediateChargePrice", currency);

    // Find fixed discounts that apply to the same SKU (for stacking)
    const fixedDiscountForSku = fixedDiscountItems
      .filter(fixed => fixed.applyToSku === promo.applyToSku)
      .reduce((sum, fixed) => sum + fixed.amount, 0);

    // Apply percentage to price after fixed discounts
    const priceAfterFixed = productPrice - fixedDiscountForSku;
    const percentageAmount = (promo.discountPercentage / 100) * priceAfterFixed;

    return {
      label: promo.couponName,
      amount: percentageAmount,
      key: promo.key,
      requiredSkus: promo.requiredSkus,
      applyToSku: promo.applyToSku,
    };
  });

  // Combine all discounts in stacking order (fixed first, then percentage)
  return [...fixedDiscountItems, ...percentageDiscountItems];
}

// Keep the old function for backward compatibility, but now it returns the first discount
export function getBundleDiscount(
  items: { sku: string; quantity: number }[],
  currency: SupportedCurrency = "usd",
) {
  const discounts = getBundleDiscounts(items, currency);
  return discounts.length > 0 ? discounts[0] : null;
}

// TODO: remove since it should be unused after cart_info refactor
// export function calculateCartTotals({
//   items,
//   accessoryProducts,
//   selectedShipping,
//   taxCents = 0,
//   couponAmountOff = 0,
//   couponPercentOff,
//   currency = "usd",
//   organizationProducts = {},
//   couponData,
// }: {
//   items: CartItem[];
//   accessoryProducts: AccessoryProduct[];
//   selectedShipping?: ShipmentRate;
//   taxCents?: number;
//   couponAmountOff?: number;
//   couponPercentOff?: number;
//   currency?: SupportedCurrency;
//   organizationProducts?: OrganizationProducts;
//   couponData?: CouponData;
// }) {
//   const productSubtotal = items.reduce((acc, { sku, quantity }) => {
//     const product = PRODUCTS[sku];
//     if (!product) return acc;
//     const price = getPrice(product, "immediateChargePrice", currency);
//     return acc + price * quantity;
//   }, 0);

//   const accessorySubtotal = items.reduce((acc, { sku, quantity }) => {
//     const accessory = accessoryProducts?.find((p) => p.SKU === sku);
//     if (!accessory) return acc;
//     return acc + accessory.PriceTier1 * quantity;
//   }, 0);

//   const shippingSubtotal = selectedShipping ? selectedShipping.totalCost : 0;

//   const total = productSubtotal + accessorySubtotal + shippingSubtotal;
//   const tax = taxCents / 100;

//   const bundleDiscounts = getBundleDiscounts(items, currency);
//   console.log({ bundleDiscounts });
//   const bundleDiscountAmount = bundleDiscounts.reduce((sum, discount) => sum + discount.amount, 0);

//   // Keep backward compatibility
//   const bundleDiscount = bundleDiscounts.length > 0 ? bundleDiscounts[0] : null;

//   // Calculate cross-product discounts
//   const cartSkus = items.map((item) => item.sku);
//   const allDiscounts = calculateAllDiscounts(cartSkus, organizationProducts, currency);

//   // Get cross-product discount amount (excluding bundle products)
//   const crossProductDiscountAmount = Object.keys(allDiscounts.crossProductDiscounts).reduce(
//     (totalDiscount, sku) => {
//       const discountDetails = allDiscounts.crossProductDiscounts[sku];
//       // Only apply the cross-product discount if the subscription will be charged immediately (not a bundle)
//       if (!discountDetails.bundle && discountDetails.amount > 0) {
//         return totalDiscount + discountDetails.amount;
//       }
//       return totalDiscount;
//     },
//     0,
//   );

//   // Calculate coupon discount
//   // For percentage-based coupons:
//   //   - If appliesTo is specified, calculate subtotal of only those items
//   //   - Subtract any bundle discounts that apply to those specific SKUs
//   //   - Apply percentage to that reduced amount
//   // For fixed amount coupons: use the fixed amount (converted from cents)
//   let coupon = 0;
//   if (couponPercentOff && couponPercentOff > 0) {
//     let applicableSubtotal = 0;

//     // If couponData has appliesTo, only calculate subtotal for those items
//     if (couponData?.appliesTo && couponData.appliesTo.length > 0) {
//       // Calculate subtotal of items that the coupon applies to
//       applicableSubtotal = items.reduce((acc, { sku, quantity }) => {
//         if (couponData.appliesTo!.includes(sku)) {
//           const product = PRODUCTS[sku];
//           if (product) {
//             const price = getPrice(product, "immediateChargePrice", currency);
//             return acc + price * quantity;
//           }
//           // Also check accessories
//           const accessory = accessoryProducts?.find((p) => p.SKU === sku);
//           if (accessory) {
//             return acc + accessory.PriceTier1 * quantity;
//           }
//         }
//         return acc;
//       }, 0);

//       // Find bundle discounts that apply to these specific SKUs
//       const applicableSkus = items
//         .filter((item) => couponData.appliesTo!.includes(item.sku))
//         .map((item) => item.sku);

//       const applicableBundleDiscounts = bundleDiscounts.filter((discount) => {
//         // Check applyToSku field first (more specific)
//         if (discount.applyToSku) {
//           return applicableSkus.includes(discount.applyToSku);
//         }

//         // Fall back to checking if any required SKU is in our applicable SKUs
//         return discount.requiredSkus.some((reqSku: string) => applicableSkus.includes(reqSku));
//       });

//       const applicableBundleDiscountAmount = applicableBundleDiscounts.reduce(
//         (sum, discount) => sum + discount.amount,
//         0,
//       );

//       // Subtract bundle discounts from applicable subtotal, then apply percentage
//       const subtotalAfterBundleDiscount = applicableSubtotal - applicableBundleDiscountAmount;
//       coupon = (subtotalAfterBundleDiscount * couponPercentOff) / 100;
//     } else {
//       // If no appliesTo specified, use the old behavior (apply to entire cart after other discounts)
//       const totalAfterOtherDiscounts = total - bundleDiscountAmount - crossProductDiscountAmount;
//       coupon = (totalAfterOtherDiscounts * couponPercentOff) / 100;
//     }
//   } else if (couponAmountOff && couponAmountOff > 0) {
//     coupon = couponAmountOff / 100;
//   }

//   const totalWithTax = total + tax - coupon - bundleDiscountAmount - crossProductDiscountAmount;

//   return {
//     productSubtotal,
//     accessorySubtotal,
//     shippingSubtotal,
//     total,
//     tax,
//     coupon,
//     bundleDiscountAmount,
//     bundleDiscounts,
//     crossProductDiscountAmount,
//     allDiscounts,
//     totalWithTax,
//     hasBundleDiscount: bundleDiscounts.length > 0,
//     bundleLabel: bundleDiscount?.label || null,
//   };
// }
